import React, { useState } from "react";
import {
  X,
  MapPin,
  Zap,
  Flame,
  AlertTriangle,
  TrendingDown,
  Scale,
  Clock,
  Home,
  ChevronDown,
  ChevronUp,
  Info,
  Ruler,
  FileText,
  Landmark,
  Banknote,
  CalendarDays,
} from "lucide-react";
import "./app.css";

/* ─── currency formatter ─── */
const fmt = (n) =>
  n != null
    ? Number(n).toLocaleString("pl-PL", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }) + " zł"
    : "—";

const pct = (n) => (n != null ? (n * 100).toFixed(0) + "%" : "—");

/* ─── collapsible section ─── */
const Section = ({ icon: Icon, title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="sn-section">
      <button className="sn-section-header" onClick={() => setOpen(!open)}>
        <div className="sn-section-title">
          {Icon && <Icon size={16} />}
          <span>{title}</span>
        </div>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && <div className="sn-section-body">{children}</div>}
    </div>
  );
};

/* ─── key-value row ─── */
const KV = ({ label, value, accent = false }) => (
  <div className="sn-kv">
    <span className="sn-kv-label">{label}</span>
    <span className={`sn-kv-value ${accent ? "sn-kv-value--accent" : ""}`}>
      {value || "—"}
    </span>
  </div>
);

/* ─── claim card ─── */
const ClaimCard = ({ icon: Icon, title, amount, desc }) => (
  <div className="sn-claim">
    <div className="sn-claim-icon">
      <Icon size={20} />
    </div>
    <div className="sn-claim-body">
      <div className="sn-claim-title">{title}</div>
      <div className="sn-claim-amount">{fmt(amount)}</div>
      <div className="sn-claim-desc">{desc}</div>
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════ */

const ShowNote = (props) => {
  const opt = props.option;
  const noteText =
    props.mode === "note" ? props.lead?.statusNote : props.lead?.sourceNote;

  /* try parse JSON for rich views */
  let data = null;
  let viewType = "plain"; // "plain" | "sluzebnosc" | "wibor"

  if (props.mode === "source" && noteText) {
    try {
      const parsed = JSON.parse(noteText);
      const key =
        props.lead?.company === "SPRZEDAZ" ? props.lead?.subCompany : opt;

      if (key === "SLUZEBNOSC" && parsed.dzialka) {
        data = parsed;
        viewType = "sluzebnosc";
      } else if (["SKD", "CHF", "WIBOR"].includes(key) && parsed.bank) {
        data = parsed;
        viewType = "wibor";
      }
    } catch {
      /* not JSON — show as plain */
    }
  }

  const isRich = viewType !== "plain";

  const modalTitle =
    viewType === "sluzebnosc"
      ? "Analiza służebności przesyłu"
      : viewType === "wibor"
      ? "Dane kredytu " + opt
      : props.mode === "source"
      ? "Notatka — źródło"
      : "Notatka";

  /* WIBOR helpers */
  const fmtCurrency = (amount, currency) => {
    if (!amount) return "—";
    return (
      Number(amount).toLocaleString("pl-PL", { minimumFractionDigits: 2 }) +
      (currency ? ` ${currency}` : " zł")
    );
  };
  const fmtMonths = (m) => {
    if (!m) return "—";
    const months = Number(m);
    const years = Math.floor(months / 12);
    const rest = months % 12;
    if (years && rest) return `${years} lat ${rest} mies. (${months} mies.)`;
    if (years) return `${years} lat (${months} mies.)`;
    return `${months} mies.`;
  };

  const toYMD = (d) => new Date(d).toISOString().slice(0, 10);

  return (
    <div
      className="sn-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) props.close();
      }}
    >
      <div className={`sn-modal ${isRich ? "sn-modal--wide" : ""}`}>
        {/* ── Header ── */}
        <div className="sn-header">
          <div>
            <h2 className="sn-title">{modalTitle}</h2>
            <span className="sn-subtitle">Kontakt: {props.lead?.name}</span>
          </div>
          <button className="ld-close" onClick={props.close}>
            <X size={18} />
          </button>
        </div>

        {/* ── Body ── */}
        <div className="sn-body">
          {viewType === "wibor" && data && (
            /* ═══ WIBOR VIEW ═══ */
            <>
              <div className="sn-wibor-hero">
                <div className="sn-wibor-bank">
                  <Landmark size={22} />
                  <span>{data.bank || "Nieznany bank"}</span>
                </div>
              </div>

              <div className="sn-wibor-grid">
                <div className="sn-wibor-card">
                  <div className="sn-wibor-card-icon sn-wibor-card-icon--amount">
                    <Banknote size={20} />
                  </div>
                  <div className="sn-wibor-card-label">Kwota kredytu</div>
                  <div className="sn-wibor-card-value">
                    {fmtCurrency(
                      data.ammount.toString().replace(" ", ""),
                      data.currency
                    )}
                  </div>
                </div>

                <div className="sn-wibor-card">
                  <div className="sn-wibor-card-icon sn-wibor-card-icon--date">
                    <CalendarDays size={20} />
                  </div>
                  <div className="sn-wibor-card-label">Data zawarcia</div>
                  <div className="sn-wibor-card-value">
                    {toYMD(data.date) || "—"}
                  </div>
                </div>

                <div className="sn-wibor-card">
                  <div className="sn-wibor-card-icon sn-wibor-card-icon--length">
                    <Clock size={20} />
                  </div>
                  <div className="sn-wibor-card-label">Okres kredytowania</div>
                  <div className="sn-wibor-card-value">
                    {fmtMonths(data.length)}
                  </div>
                </div>

                {data.margin && (
                  <div className="sn-wibor-card">
                    <div className="sn-wibor-card-icon sn-wibor-card-icon--margin">
                      <TrendingDown size={20} />
                    </div>
                    <div className="sn-wibor-card-label">Marża</div>
                    <div className="sn-wibor-card-value">{data.margin}%</div>
                  </div>
                )}
              </div>

              {data.currency && (
                <div className="sn-wibor-footer">
                  <span className="sn-infra-badge sn-infra-badge--dim">
                    {data.currency}
                  </span>
                </div>
              )}
            </>
          )}

          {viewType === "sluzebnosc" && data && (
            /* ═══ RICH JSON VIEW ═══ */
            <>
              {/* Hero: plot + address */}
              <div className="sn-hero">
                <div className="sn-hero-item">
                  <MapPin size={18} />
                  <div>
                    <div className="sn-hero-label">Działka</div>
                    <div className="sn-hero-value">{data.dzialka}</div>
                  </div>
                </div>
                <div className="sn-hero-item">
                  <Home size={18} />
                  <div>
                    <div className="sn-hero-label">Adres</div>
                    <div className="sn-hero-value">{data.adres}</div>
                  </div>
                </div>
                {data.suma_total && (
                  <div className="sn-hero-item sn-hero-item--total">
                    <Scale size={18} />
                    <div>
                      <div className="sn-hero-label">Suma roszczeń</div>
                      <div className="sn-hero-value sn-hero-value--big">
                        {fmt(data.suma_total)}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Technical analysis */}
              {data.analiza_techniczna && (
                <Section icon={FileText} title="Analiza techniczna">
                  <p className="sn-text">{data.analiza_techniczna}</p>
                </Section>
              )}

              {/* Infrastructure */}
              {data.infrastruktura && (
                <Section icon={Zap} title="Infrastruktura">
                  {data.infrastruktura.linie_elektroenergetyczne?.wykryte && (
                    <div className="sn-infra-card">
                      <div className="sn-infra-icon sn-infra-icon--elec">
                        <Zap size={16} />
                      </div>
                      <div className="sn-infra-body">
                        <div className="sn-infra-title">
                          Linie elektroenergetyczne
                          <span className="sn-infra-badge">
                            {
                              data.infrastruktura.linie_elektroenergetyczne
                                .oznaczenie
                            }
                          </span>
                        </div>
                        <div className="sn-infra-details">
                          <KV
                            label="Strefa ochronna"
                            value={`${data.infrastruktura.linie_elektroenergetyczne.strefa_ochronna_m} m`}
                          />
                          {data.infrastruktura.linie_elektroenergetyczne.linie?.map(
                            (l, i) => (
                              <KV
                                key={i}
                                label={`Linia ${i + 1} — długość`}
                                value={l.dlugosc_m ? `${l.dlugosc_m} m` : "—"}
                              />
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  {data.infrastruktura.linie_gazowe?.wykryte && (
                    <div className="sn-infra-card">
                      <div className="sn-infra-icon sn-infra-icon--gas">
                        <Flame size={16} />
                      </div>
                      <div className="sn-infra-body">
                        <div className="sn-infra-title">
                          Linie gazowe
                          <span className="sn-infra-badge">
                            {data.infrastruktura.linie_gazowe.oznaczenie}
                          </span>
                          {data.infrastruktura.linie_gazowe.dn && (
                            <span className="sn-infra-badge sn-infra-badge--dim">
                              DN {data.infrastruktura.linie_gazowe.dn}
                            </span>
                          )}
                        </div>
                        <div className="sn-infra-details">
                          <KV
                            label="Strefa ochronna"
                            value={`${data.infrastruktura.linie_gazowe.strefa_ochronna_m} m`}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  {data.infrastruktura.uwagi && (
                    <div className="sn-note-box">
                      <Info size={14} />{" "}
                      <span>{data.infrastruktura.uwagi}</span>
                    </div>
                  )}
                </Section>
              )}

              {/* Parameters */}
              {data.parametry && (
                <Section icon={Ruler} title="Parametry">
                  <div className="sn-params-grid">
                    <KV
                      label="Powierzchnia działki"
                      value={`${data.parametry.powierzchnia_dzialki_m2?.toLocaleString(
                        "pl-PL"
                      )} m²`}
                    />
                    <KV
                      label="Powierzchnia strefy"
                      value={`${data.parametry.powierzchnia_strefy_m2?.toLocaleString(
                        "pl-PL"
                      )} m²`}
                    />
                    <KV
                      label="Szerokość strefy"
                      value={`${data.parametry.szerokosc_strefy_m} m`}
                    />
                    <KV
                      label="Współczynnik K"
                      value={data.parametry.wspolczynnik_k}
                      accent
                    />
                  </div>
                  {data.parametry.wyjasnienie_k && (
                    <div className="sn-note-box">
                      <Info size={14} />{" "}
                      <span>{data.parametry.wyjasnienie_k}</span>
                    </div>
                  )}
                </Section>
              )}

              {/* Price drop explanation */}
              {data.wyjasnienie_algorytmu_spadku && (
                <Section
                  icon={TrendingDown}
                  title="Spadek wartości"
                  defaultOpen={false}
                >
                  <p className="sn-text">{data.wyjasnienie_algorytmu_spadku}</p>
                </Section>
              )}

              {/* Claims */}
              {data.roszczenia && (
                <Section icon={Scale} title="Roszczenia">
                  <div className="sn-claims-grid">
                    {data.roszczenia.ustanowienie_sluzebnosci && (
                      <ClaimCard
                        icon={Scale}
                        title="Ustanowienie służebności"
                        amount={data.roszczenia.ustanowienie_sluzebnosci.kwota}
                        desc={data.roszczenia.ustanowienie_sluzebnosci.opis}
                      />
                    )}
                    {data.roszczenia.bezumowne_korzystanie_6lat && (
                      <ClaimCard
                        icon={Clock}
                        title="Bezumowne korzystanie (6 lat)"
                        amount={
                          data.roszczenia.bezumowne_korzystanie_6lat.kwota
                        }
                        desc={data.roszczenia.bezumowne_korzystanie_6lat.opis}
                      />
                    )}
                    {data.roszczenia.szkoda_lokalizacyjna && (
                      <ClaimCard
                        icon={TrendingDown}
                        title="Szkoda lokalizacyjna"
                        amount={data.roszczenia.szkoda_lokalizacyjna.kwota}
                        desc={data.roszczenia.szkoda_lokalizacyjna.opis}
                      />
                    )}
                  </div>
                  {data.suma_total && (
                    <div className="sn-total-bar">
                      <span>SUMA ROSZCZEŃ</span>
                      <span className="sn-total-amount">
                        {fmt(data.suma_total)}
                      </span>
                    </div>
                  )}
                </Section>
              )}
            </>
          )}

          {viewType === "plain" && (
            <div className="sn-plain">
              <p>{noteText || "Brak treści"}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShowNote;
